import { useCallback, useEffect, useState } from "react";
import { useFocusEffect } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/reducers";
import { DomusoApiService } from "../api/DomusoApiService";
import {
  addReservations,
  setAllowServiceRequests,
  updateAppMode,
  setBlacklist,
  setBlacklistRecovery,
  setSuperBlacklist,
  setRecoveryPaymentCleared,
} from "../redux/actions/actionCreators";
import { AccountSystemTypes, AppMode, UserRoles } from "../enums";
import { StorageKeys } from "../enums/storage-keys";
import * as storage from "../utils/storage";
import { isBalanceError } from "../utils/money";

const INTEGRATED = "INTEGRATED";
const recoveryPaymentTooBigThreshold = 1000000;

export const useReservationInformation = (caller = null, shouldCallBackend = false) => {
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const isBlacklisted = useSelector((state: RootState) => state.reservation.isBlacklisted);
  const isBlacklistedRecovery = useSelector(
    (state: RootState) => state.reservation.isBlacklistedRecovery
  );
  const isSuperBlackList = useSelector((state: RootState) => state.reservation.isSuperBlacklist);
  const isRecoveryPaymentCleared = useSelector(
    (state: RootState) => state.reservation.isRecoveryPaymentCleared
  );
  const logger = useSelector((state: RootState) => state.logger);
  const [reservations, setReservations] = useState<any>();
  const [hasError, setHasError] = useState<any>();

  const [firstReservationId, setFirstReservationId] = useState<any>();
  const preloadedReservations = useSelector((state: RootState) => state.reservation.reservations);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);

  logger.info(`useReservationInformation Caller: ${caller}`);

  const getFormattedReservations = async (res: any[]) => {
    let recoveryPaymentRunningTotal = 0;
    let superBlackListRecoveryPaymentNullIDsFlag = false;
    res = res.filter(({ status }: any) => status !== "CANCELLED_WITHOUT_FEE");

    return res.map(
      ({
        property_unit,
        property_fee_profile,
        property_profile,
        property,
        accounting_id,
        service_request_type,
        active_auto_pay,
        allowServiceRequests,
        ...reservationInfo
      }: any) => {
        if (reservationInfo.is_blacklisted) {
          if (reservationInfo.recovery_payments.length > 0) {
            dispatch(setRecoveryPaymentCleared(false));
          }
          dispatch(setBlacklistRecovery(true));
          dispatch(setBlacklist(true));
        } else {
          dispatch(setBlacklist(false));
          dispatch(setBlacklistRecovery(false));
          dispatch(setRecoveryPaymentCleared(true));
        }

        const isAIM = property_profile.account_system_type === AccountSystemTypes.AIM;
        const isYardi = property_profile.account_system_type === AccountSystemTypes.YARDI;
        const isServiceRequestIntegrated = service_request_type === INTEGRATED;
        const allowSR =
          ((isAIM || isYardi) && accounting_id && property_unit && isServiceRequestIntegrated) ||
          allowServiceRequests;

        if (allowSR) {
          logger.info(`Service request allowed for resident`);
          dispatch(setAllowServiceRequests(true));
          storage.save(StorageKeys.ALLOW_SERVICE_REQUESTS, true);
        }

        reservationInfo.recovery_payments.forEach((item: any) => {
          recoveryPaymentRunningTotal += item.total;

          if (!item.id) {
            superBlackListRecoveryPaymentNullIDsFlag = true;
          }

          if (!item.order_id) {
            superBlackListRecoveryPaymentNullIDsFlag = true;
          }
        });

        if (recoveryPaymentRunningTotal > recoveryPaymentTooBigThreshold) {
          logger.info(
            `Super blacklist due to recovery payments total is greater on the accepted ${recoveryPaymentTooBigThreshold} limit `
          );
          dispatch(setSuperBlacklist(true));
        }

        if (superBlackListRecoveryPaymentNullIDsFlag) {
          logger.info(`Super blacklist due existing blacklist_reservation row with no order_id`);
          dispatch(setSuperBlacklist(true));
        }

        let resp = {
          ...reservationInfo,
          accounting_id,
          active_auto_pay,
          prop_name: property.name,
          property_fee_profile,
          property_profile,
          property,
          property_unit,
          allowServiceRequests: allowSR,
        };
        logger.info(`Using the following reservation info`, {
          ...resp,
        });
        return resp;
      }
    );
  };

  const layoutEffect = useCallback(() => {
    (async () => {
      try {
        if (!shouldCallBackend && preloadedReservations && preloadedReservations.length) {
          const res = await getFormattedReservations(preloadedReservations);
          setReservations(res);
          setFirstReservationId(res[0]?.id);
          return;
        }
        const resp = await domusoApiService.getReservations();
        if (resp && Array.isArray(resp) && resp.length > 0) {
          // if there the API is temporarily unavailable it might return an object with a slightly different
          // structure: { is_blocked: true, balanceDue: "Temporarily unavailable" }
          if (
            resp.length == 1 &&
            resp[0].balanceDue !== undefined &&
            isBalanceError(resp[0].balanceDue)
          ) {
            logger.error(new Error("API temporarily unavailable"));
            setHasError(true);
          } else {
            const modifiedResp = resp?.map((item) => {
              const payload = item;

              if (item?.future_balance_due) {
                item.balance_due = item.future_balance_due;
                item.accounting_details.balanceDue = item.future_balance_due;
              }

              return payload;
            });

            let res = await getFormattedReservations(modifiedResp);

            setReservations(res);
            dispatch(addReservations(res));
            setFirstReservationId(res[0]?.id);
            setHasError(false);
          }
        } else if (resp?.message == "Internal server error") {
          logger.error(new Error("Internal server error"));
          setHasError(true);
        } else if (resp?.message == "Endpoint request timed out") {
          logger.error(new Error(resp.message));
          setHasError(true);
        } else {
          setReservations([]);
          setHasError(true);
        }
      } catch (error) {
        setHasError(true);
        logger.error(error);
      }
    })();
  }, []);
  useFocusEffect(layoutEffect);

  useEffect(() => {
    if (isBlacklistedRecovery) {
      //isRecoveryPaymentCleared is set to false in getFormattedReservations() when reservation.is_blacklisted = true and reservationInfo.recovery_payments.length > 0 else is undefined
      if (isRecoveryPaymentCleared === false) {
        //This happens when a user is blackedlisted in ops portal with an order id.
        //User will see the dcash recovery page, once it makes the payment it will be redirected to the normal home page.
        dispatch(updateAppMode(AppMode.DCASH_RECOVERY));
      } else {
        //Is possible that user was blacklisted without an order id in which case user will see a message in home page "Account Locked...".
        //Since there is no order id we don't allow the user to make payments, manager needs to request to be manually removed from the blacklist.
        dispatch(updateAppMode(AppMode.NORMAL));
      }
    }
  }, [isBlacklistedRecovery]);

  return {
    reservations,
    firstReservationId,
    isBlacklist: isBlacklisted,
    isSuperBlackList,
    hasError,
  };
};
